import React, { Component } from "react";
import { Input } from "reactstrap";
import {
  getBannerById,
  createBanner,
  updateBanner,
  getCouponList,
  getBannerList,
} from "../../Endpoint/Admin/index";
import { toast } from "react-toastify";
import { ConsolePage } from "../../components/Console/ConsoleLayout";
import { C, Panel, Btn, Field, Toggle, Thumb } from "../../components/Console/ConsoleUI";
import { canWrite } from "../../services/PermissionService";

/**
 * Banner editor — the companion to screen E4·17. The handoff draws the banner
 * manager but not its editor, so this borrows the coupon editor's language
 * (E3·16): one page of panels beside a live preview of what the guest sees.
 *
 * A three-step wizard for four fields and a picture forced you to page forward
 * to find out whether the artwork you chose looked right. Everything is on
 * screen at once now.
 *
 * Re-skin only: the FormData body, the multipart upload, the date rules and the
 * unique-slot check are exactly the ones the wizard used.
 */

const CSS = `
.banner-editor .form-control,.banner-editor .custom-select{height:48px;padding:0 15px;
  background:${C.surface};border:1px solid ${C.border};border-radius:12px;font-size:15px;
  color:${C.ink};box-shadow:none}
.banner-editor .form-control:focus{background:#fff;border-color:${C.sand};box-shadow:none}
.banner-editor .form-control::placeholder{color:${C.taupeLight}}
.banner-editor textarea.form-control{height:auto;min-height:78px;padding:12px 15px;
  line-height:1.5;font-size:14px}
.banner-chip{padding:9px 16px;border-radius:19px;font-size:14px;font-family:inherit;cursor:pointer;
  background:#fff;border:1px solid ${C.border};color:${C.taupe};line-height:1.3}
.banner-chip.is-on{background:${C.brown};border-color:transparent;color:#fff}
.banner-layout{flex:1;padding:16px;border-radius:14px;border:1px solid ${C.border};background:#fff;
  cursor:pointer;font-family:inherit;text-align:left}
.banner-layout.is-on{border-color:${C.brown};background:${C.iconTint}}
`;

const THEME_HEX = {
  red: "#E53935", blue: "#1E88E5", green: "#43A047",
  orange: "#FF9800", purple: "#8E24AA", dark: "#37474F",
};

const SelChip = ({ on, onClick, children }) => (
  <button type="button" className={`banner-chip${on ? " is-on" : ""}`} onClick={onClick}>
    {children}
  </button>
);

class BannerForm extends Component {
  constructor(props) {
    super(props);
    const bannerId = this.props.match?.params?.id;

    this.state = {
      bannerId: bannerId || null,
      isEditMode: !!bannerId,
      isLoading: bannerId ? true : false,

      // Dropdown data
      coupons: [],

      // Basic Info + Image
      title: "",
      description: "",
      bannerImage: null,
      imagePreview: null,
      existingImageUrl: null,

      // Link Settings
      linkType: "none",
      linkedCouponId: "",
      externalUrl: "",
      internalRoute: "",

      // Schedule & Display
      displayOrder: 0,
      startDate: "",
      endDate: "",
      isActive: true,

      // Validation helpers
      usedDisplayOrders: [],
      bannerOrderMap: {}, // Map of bannerId -> displayOrder for edit mode validation
      displayOrderError: "",

      // Banner layout options
      layoutType: "split", // 'split' = text left/image right, 'fullImage' = just image
      ctaText: "View Now",
      bgTheme: "red", // red, blue, green, orange, purple, dark
    };

    this.fileInputRef = React.createRef();
  }

  componentDidMount() {
    this.loadCoupons();
    this.loadExistingBanners();
    if (this.state.bannerId) {
      this.loadBanner();
    }
  }

  loadExistingBanners = () => {
    getBannerList({})
      .then((result) => {
        if (result.status === 200) {
          const banners = result.data || [];
          const usedDisplayOrders = banners.map(b => b.displayOrder);
          // Create a map of bannerId -> displayOrder for edit validation
          const bannerOrderMap = {};
          banners.forEach(b => {
            bannerOrderMap[b._id] = b.displayOrder;
          });

          // For new banners, suggest the next available order (unique)
          if (!this.state.bannerId) {
            const maxOrder = usedDisplayOrders.length > 0 ? Math.max(...usedDisplayOrders) : -1;
            this.setState({
              usedDisplayOrders,
              bannerOrderMap,
              displayOrder: maxOrder + 1,
            });
          } else {
            this.setState({ usedDisplayOrders, bannerOrderMap });
          }
        }
      })
      .catch(() => console.log("Error loading banners for display order"));
  };

  loadCoupons = () => {
    getCouponList({ status: "active" })
      .then((result) => {
        if (result.status === 200) {
          this.setState({ coupons: result.data || [] });
        }
      })
      .catch(() => console.log("Error loading coupons"));
  };

  loadBanner = () => {
    getBannerById(this.state.bannerId)
      .then((result) => {
        if (result.status === 200) {
          const banner = result.data;
          this.setState({
            isLoading: false,
            title: banner.title || "",
            description: banner.description || "",
            existingImageUrl: banner.imageUrl || null,
            linkType: banner.linkType || "none",
            linkedCouponId: banner.linkedCouponId?._id || banner.linkedCouponId || "",
            externalUrl: banner.externalUrl || "",
            internalRoute: banner.internalRoute || "",
            displayOrder: banner.displayOrder || 0,
            startDate: banner.startDate ? banner.startDate.split("T")[0] : "",
            endDate: banner.endDate ? banner.endDate.split("T")[0] : "",
            isActive: banner.isActive !== false,
            layoutType: banner.layoutType || "split",
            ctaText: banner.ctaText || "View Now",
            bgTheme: banner.bgTheme || "red",
          });
        } else {
          toast.error(result.message || "Failed to load banner");
          this.props.history.push("/banner");
        }
      })
      .catch(() => {
        toast.error("Error loading banner");
        this.props.history.push("/banner");
      });
  };

  handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === "checkbox" ? checked : value;

    // Date validation
    if (name === "startDate" && value) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const selectedDate = new Date(value);

      // For new banners, don't allow past dates
      if (!this.state.bannerId && selectedDate < today) {
        toast.error("Start date cannot be in the past");
        return;
      }

      // If end date exists and is before new start date, clear end date
      if (this.state.endDate && new Date(this.state.endDate) < selectedDate) {
        this.setState({ endDate: "" });
        toast.warning("End date was cleared because it was before the new start date");
      }
    }

    if (name === "endDate" && value) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const selectedDate = new Date(value);
      const startDate = this.state.startDate ? new Date(this.state.startDate) : today;

      // End date cannot be before start date (or today if no start date)
      if (selectedDate < startDate) {
        toast.error("End date cannot be before start date");
        return;
      }

      // End date cannot be in the past
      if (selectedDate < today) {
        toast.error("End date cannot be in the past");
        return;
      }
    }

    this.setState({
      [name]: newValue,
    }, () => {
      // Check for display order conflicts
      if (name === "displayOrder") {
        this.checkDisplayOrderConflict(parseInt(newValue));
      }
    });
  };

  checkDisplayOrderConflict = (orderValue) => {
    const { bannerOrderMap, bannerId } = this.state;

    // Check if this order is used by another banner
    let isOrderTaken = false;
    for (const [bId, bOrder] of Object.entries(bannerOrderMap)) {
      if (bOrder === orderValue && bId !== bannerId) {
        isOrderTaken = true;
        break;
      }
    }

    this.setState({
      displayOrderError: isOrderTaken ? `Display order #${orderValue} is already taken. Choose a unique number.` : "",
    });
  };

  handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
      if (!validTypes.includes(file.type)) {
        toast.error("Please select a valid image (JPEG, PNG, or WebP)");
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image size must be less than 5MB");
        return;
      }

      this.setState({
        bannerImage: file,
        imagePreview: URL.createObjectURL(file),
      });
    }
  };

  removeImage = () => {
    if (this.state.imagePreview) {
      URL.revokeObjectURL(this.state.imagePreview);
    }
    this.setState({
      bannerImage: null,
      imagePreview: null,
      existingImageUrl: null,
    });
    if (this.fileInputRef.current) {
      this.fileInputRef.current.value = "";
    }
  };

  /**
   * The wizard's three step validators run together, since every field is on
   * screen at once now. Same conditions, same messages, same order.
   */
  validateAll = () => {
    const {
      title, bannerImage, existingImageUrl, bannerId,
      linkType, linkedCouponId, externalUrl, internalRoute,
      displayOrder, bannerOrderMap,
    } = this.state;

    if (!title.trim()) {
      toast.error("Please enter a banner title");
      return false;
    }
    if (!bannerId && !bannerImage && !existingImageUrl) {
      toast.error("Please upload a banner image");
      return false;
    }

    // Link type validation - if a type is selected, the corresponding field is required
    if (linkType === "coupon" && !linkedCouponId) {
      toast.error("Please select a coupon or change link type to 'No Action'");
      return false;
    }
    if (linkType === "external" && !externalUrl.trim()) {
      toast.error("Please enter an external URL or change link type to 'No Action'");
      return false;
    }
    if (linkType === "internal" && !internalRoute) {
      toast.error("Please select an app route or change link type to 'No Action'");
      return false;
    }

    // Display order must be unique
    const orderNum = parseInt(displayOrder);
    let isOrderTaken = false;
    for (const [bId, bOrder] of Object.entries(bannerOrderMap)) {
      if (bOrder === orderNum && bId !== bannerId) {
        isOrderTaken = true;
        break;
      }
    }
    if (isOrderTaken) {
      toast.error(`Display order #${orderNum} is already used by another banner. Please choose a unique number.`);
      return false;
    }
    return true;
  };

  handleSubmit = () => {
    if (!this.validateAll()) return;

    const {
      bannerId,
      title,
      description,
      bannerImage,
      linkType,
      linkedCouponId,
      externalUrl,
      internalRoute,
      displayOrder,
      startDate,
      endDate,
      isActive,
      layoutType,
      ctaText,
      bgTheme,
    } = this.state;

    // Date validations
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (startDate) {
      const start = new Date(startDate);
      // Don't allow past start dates for new banners
      if (!bannerId && start < today) {
        toast.error("Start date cannot be in the past");
        return;
      }
    }

    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      if (end < start) {
        toast.error("End date must be after start date");
        return;
      }
    }

    // Create form data for file upload
    const formData = new FormData();
    formData.append("title", title);
    formData.append("description", description);
    formData.append("linkType", linkType);
    formData.append("placement", "home");
    formData.append("displayOrder", displayOrder.toString());
    formData.append("isActive", isActive.toString());
    formData.append("layoutType", layoutType);
    formData.append("ctaText", ctaText);
    formData.append("bgTheme", bgTheme);

    if (bannerImage) {
      formData.append("bannerImage", bannerImage);
    }

    if (linkType === "coupon" && linkedCouponId) {
      formData.append("linkedCouponId", linkedCouponId);
    }
    if (linkType === "external" && externalUrl) {
      formData.append("externalUrl", externalUrl);
    }
    if (linkType === "internal" && internalRoute) {
      formData.append("internalRoute", internalRoute);
    }
    if (startDate) {
      formData.append("startDate", startDate);
    }
    if (endDate) {
      formData.append("endDate", endDate);
    }

    const apiCall = bannerId ? updateBanner(bannerId, formData) : createBanner(formData);

    apiCall
      .then((response) => {
        if (response && response.status === 200) {
          toast.success(bannerId ? "Banner updated!" : "Banner created!");
          setTimeout(() => this.props.history.push("/banner"), 1500);
        } else {
          toast.error(response?.message || "Failed to save banner");
        }
      })
      .catch((err) => {
        console.error(err);
        toast.error("Error saving banner");
      });
  };

  getLinkedCouponName = () => {
    const { linkedCouponId, coupons } = this.state;
    if (!linkedCouponId) return null;
    const coupon = coupons.find(c => c._id === linkedCouponId);
    return coupon ? `${coupon.code} - ${coupon.discountValue}${coupon.discountType === 'percentage' ? '%' : '$'}` : null;
  };

  currentImage() {
    const { imagePreview, existingImageUrl } = this.state;
    // Get image URL - for server images, prepend API base URL
    const serverImageUrl = existingImageUrl && !existingImageUrl.startsWith("blob:") && !existingImageUrl.startsWith("http")
      ? `${process.env.REACT_APP_DEFAULTAUTH}${existingImageUrl}`
      : existingImageUrl;
    return imagePreview || serverImageUrl;
  }

  renderBasics() {
    const { title, description, layoutType, ctaText, bgTheme, linkType } = this.state;
    const write = canWrite();
    const image = this.currentImage();

    return (
      <Panel title="Banner basics">
        <div style={{ marginTop: 18, display: "flex", gap: 18, alignItems: "flex-start" }}>
          <div style={{ flex: "0 0 240px" }}>
            <div style={{ fontSize: 12, letterSpacing: ".6px", textTransform: "uppercase", color: C.taupe, marginBottom: 7 }}>
              Artwork
            </div>
            <input
              type="file"
              ref={this.fileInputRef}
              accept="image/jpeg,image/jpg,image/png,image/webp"
              onChange={this.handleImageChange}
              style={{ display: "none" }}
            />
            {image ? (
              <div>
                <div style={{ position: "relative", borderRadius: 14, overflow: "hidden" }}>
                  <Thumb src={image} alt="Banner artwork" height={110} />
                  {write && (
                    <button
                      type="button"
                      onClick={this.removeImage}
                      title="Remove image"
                      style={{
                        position: "absolute", top: 8, right: 8, width: 28, height: 28,
                        borderRadius: 14, border: 0, background: "rgba(255,255,255,.92)",
                        color: C.red, cursor: "pointer", display: "grid", placeItems: "center", padding: 0,
                      }}
                    >
                      <span className="ms" style={{ fontSize: 16 }}>close</span>
                    </button>
                  )}
                </div>
                {write && (
                  <button
                    type="button"
                    onClick={() => this.fileInputRef.current?.click()}
                    style={{
                      marginTop: 10, border: 0, background: "transparent", padding: 0,
                      fontSize: 13, fontWeight: 500, color: C.brown, cursor: "pointer", fontFamily: "inherit",
                    }}
                  >
                    Replace image
                  </button>
                )}
              </div>
            ) : (
              <button
                type="button"
                disabled={!write}
                onClick={() => this.fileInputRef.current?.click()}
                style={{
                  width: "100%", height: 110, borderRadius: 14, border: "1.5px dashed #dfcdb2",
                  background: C.surface, color: C.taupeLight, cursor: write ? "pointer" : "default",
                  fontFamily: "inherit", fontSize: 13, display: "flex", flexDirection: "column",
                  alignItems: "center", justifyContent: "center", gap: 6,
                }}
              >
                <span className="ms" style={{ fontSize: 26 }}>add_photo_alternate</span>
                Upload artwork
              </button>
            )}
            <div style={{ marginTop: 8, fontSize: 12, color: C.taupeLight }}>
              1200 × 400 px · JPG, PNG or WebP · max 5 MB
            </div>
          </div>

          <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 16 }}>
            <Field label="Title" hint="Shown to guests on the card">
              <Input
                type="text"
                name="title"
                value={title}
                onChange={this.handleChange}
                placeholder="e.g. Summer Sale - 20% Off"
              />
            </Field>
            <Field label="Description">
              <Input
                type="textarea"
                name="description"
                value={description}
                onChange={this.handleChange}
                rows={2}
                placeholder="Brief description (optional)…"
              />
            </Field>
          </div>
        </div>

        <div style={{ marginTop: 22, paddingTop: 20, borderTop: `1px solid ${C.panel}` }}>
          <div style={{ fontSize: 12, letterSpacing: ".6px", textTransform: "uppercase", color: C.taupe, marginBottom: 9 }}>
            Layout
          </div>
          <div style={{ display: "flex", gap: 12 }}>
            <button
              type="button"
              className={`banner-layout${layoutType === "split" ? " is-on" : ""}`}
              onClick={() => this.setState({ layoutType: "split" })}
            >
              <div style={{ display: "flex", borderRadius: 6, overflow: "hidden", width: 78, height: 34, border: `1px solid ${C.border}` }}>
                <span style={{ width: "50%", background: THEME_HEX[bgTheme] || THEME_HEX.red }} />
                <span style={{ width: "50%", background: C.tint }} />
              </div>
              <div style={{ marginTop: 10, fontSize: 14, color: C.ink }}>Split layout</div>
              <div style={{ marginTop: 2, fontSize: 12, color: C.taupeLight }}>Text left, image right</div>
            </button>
            <button
              type="button"
              className={`banner-layout${layoutType === "fullImage" ? " is-on" : ""}`}
              onClick={() => this.setState({ layoutType: "fullImage" })}
            >
              <div style={{ width: 78, height: 34, borderRadius: 6, background: C.tint, border: `1px solid ${C.border}`, display: "grid", placeItems: "center" }}>
                <span className="ms" style={{ fontSize: 17, color: C.taupeLight }}>image</span>
              </div>
              <div style={{ marginTop: 10, fontSize: 14, color: C.ink }}>Full image</div>
              <div style={{ marginTop: 2, fontSize: 12, color: C.taupeLight }}>Pre-designed banner</div>
            </button>
          </div>

          {layoutType === "split" && (
            <div style={{ marginTop: 18, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              <Field
                label="Button text"
                hint={linkType === "none" ? "Choose a link below to show a button" : null}
              >
                <Input
                  type="text"
                  name="ctaText"
                  value={ctaText}
                  onChange={this.handleChange}
                  placeholder="View Now"
                  disabled={linkType === "none"}
                />
              </Field>
              <Field label="Panel colour">
                <div style={{ display: "flex", gap: 10, alignItems: "center", height: 48 }}>
                  {Object.keys(THEME_HEX).map((id) => (
                    <span
                      key={id}
                      title={id}
                      onClick={() => this.setState({ bgTheme: id })}
                      style={{
                        width: 30, height: 30, borderRadius: 15, cursor: "pointer",
                        background: THEME_HEX[id],
                        boxShadow: bgTheme === id ? `0 0 0 2px #fff, 0 0 0 4px ${C.brown}` : "none",
                      }}
                    />
                  ))}
                </div>
              </Field>
            </div>
          )}

          {layoutType === "fullImage" && (
            <div style={{ marginTop: 14, fontSize: 13, color: C.taupeLight }}>
              The artwork is shown as-is, with no text panel over it.
            </div>
          )}
        </div>
      </Panel>
    );
  }

  renderLink() {
    const { linkType, linkedCouponId, externalUrl, internalRoute, coupons } = this.state;

    return (
      <Panel title="Where it links" subtitle="What happens when a guest taps the banner">
        <div style={{ marginTop: 16, display: "flex", gap: 10, flexWrap: "wrap" }}>
          {[
            { id: "none", label: "Not clickable" },
            { id: "coupon", label: "Show a coupon" },
            { id: "external", label: "Open a URL" },
            { id: "internal", label: "Go to an app screen" },
          ].map((t) => (
            <SelChip key={t.id} on={linkType === t.id} onClick={() => this.setState({ linkType: t.id })}>
              {t.label}
            </SelChip>
          ))}
        </div>

        {linkType === "coupon" && (
          <div style={{ marginTop: 18 }}>
            <Field
              label="Coupon"
              error={!linkedCouponId ? "Pick a coupon, or set the banner to Not clickable." : null}
              hint="Only active coupons are listed"
            >
              <Input type="select" name="linkedCouponId" value={linkedCouponId} onChange={this.handleChange}>
                <option value="">— Select a coupon —</option>
                {coupons.map((coupon) => (
                  <option key={coupon._id} value={coupon._id}>
                    {coupon.code} - {coupon.name} (
                    {coupon.discountType === "percentage"
                      ? `${coupon.discountValue}%`
                      : `$${coupon.discountValue}`}
                    )
                  </option>
                ))}
              </Input>
            </Field>
          </div>
        )}

        {linkType === "external" && (
          <div style={{ marginTop: 18 }}>
            <Field
              label="External URL"
              error={!externalUrl.trim() ? "Enter a URL, or set the banner to Not clickable." : null}
              hint="Opens in the phone's browser"
            >
              <Input
                type="url"
                name="externalUrl"
                value={externalUrl}
                onChange={this.handleChange}
                placeholder="https://example.com"
              />
            </Field>
          </div>
        )}

        {linkType === "internal" && (
          <div style={{ marginTop: 18 }}>
            <Field
              label="App screen"
              error={!internalRoute ? "Pick a screen, or set the banner to Not clickable." : null}
            >
              <Input type="select" name="internalRoute" value={internalRoute} onChange={this.handleChange}>
                <option value="">— Select app route —</option>
                <option value="/food">Food &amp; Dining</option>
                <option value="/rooms">Rooms &amp; Booking</option>
                <option value="/amenities">Amenities</option>
                <option value="/attractions">Attractions</option>
                <option value="/transport">Transportation</option>
                <option value="/coupons">Coupons &amp; Offers</option>
              </Input>
            </Field>
          </div>
        )}

        {linkType === "none" && (
          <div style={{ marginTop: 16, fontSize: 13, color: C.taupeLight }}>
            The banner is shown but does nothing when tapped.
          </div>
        )}
      </Panel>
    );
  }

  renderSchedule() {
    const { displayOrder, startDate, endDate, isActive, displayOrderError, bannerId, usedDisplayOrders } = this.state;
    const today = new Date().toISOString().split("T")[0];
    const maxOrder = usedDisplayOrders.length > 0 ? Math.max(...usedDisplayOrders) : 0;

    return (
      <Panel title="Slot &amp; schedule">
        <div style={{ marginTop: 18, display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
          <Field
            label="Slot"
            error={displayOrderError || null}
            hint={`Lower shows first. Must be unique — highest in use is #${maxOrder}.`}
          >
            <Input
              type="number"
              name="displayOrder"
              value={displayOrder}
              onChange={this.handleChange}
              min="0"
            />
          </Field>
          <Field label="Starts" hint={bannerId ? null : "Cannot be in the past"}>
            <Input
              type="date"
              name="startDate"
              value={startDate}
              onChange={this.handleChange}
              min={!bannerId ? today : undefined}
            />
          </Field>
          <Field label="Ends" hint="Empty = no expiry">
            <Input
              type="date"
              name="endDate"
              value={endDate}
              onChange={this.handleChange}
              min={startDate || today}
            />
          </Field>
        </div>

        {usedDisplayOrders.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <div style={{ fontSize: 12, color: C.taupeLight, marginBottom: 8 }}>Slots already in use</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {[...new Set(usedDisplayOrders)].sort((a, b) => a - b).map((order) => {
                const clash = order === parseInt(displayOrder, 10);
                return (
                  <span
                    key={order}
                    style={{
                      padding: "4px 10px", borderRadius: 9, fontSize: 12, fontWeight: 500,
                      background: clash ? "rgba(192,32,43,.12)" : C.tint,
                      color: clash ? C.red : C.taupe,
                    }}
                  >
                    #{order}
                  </span>
                );
              })}
            </div>
          </div>
        )}

        <div
          style={{
            marginTop: 22, paddingTop: 18, borderTop: `1px solid ${C.panel}`,
            display: "flex", alignItems: "center", gap: 14,
          }}
        >
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, color: C.ink }}>Live in the guest app</div>
            <div style={{ marginTop: 2, fontSize: 13, color: C.taupeLight }}>
              {isActive
                ? "Appears in the home carousel, within the dates above."
                : "Hidden from guests until you switch this on."}
            </div>
          </div>
          <Toggle on={isActive} title="Active" onClick={() => this.setState({ isActive: !isActive })} />
        </div>
      </Panel>
    );
  }

  /** What the guest sees — the split layout the app actually renders. */
  renderPreview() {
    const { title, description, layoutType, ctaText, bgTheme, linkType, isActive } = this.state;
    const image = this.currentImage();
    const panel = THEME_HEX[bgTheme] || THEME_HEX.red;

    return (
      <div style={{ borderRadius: 20, background: C.brownDeep, padding: "14px 12px 18px" }}>
        <div style={{ borderRadius: 14, overflow: "hidden", height: 128, display: "flex", background: C.iconTint }}>
          {layoutType === "split" ? (
            <React.Fragment>
              <div
                style={{
                  flex: "1 1 52%", background: panel, color: "#fff",
                  padding: "14px 14px", display: "flex", flexDirection: "column", justifyContent: "center",
                  minWidth: 0,
                }}
              >
                <div style={{ fontSize: 15, fontWeight: 600, lineHeight: 1.25 }}>
                  {title || "Banner title"}
                </div>
                {description && (
                  <div style={{ marginTop: 4, fontSize: 11, opacity: 0.85, lineHeight: 1.35 }}>
                    {description.length > 60 ? `${description.slice(0, 60)}…` : description}
                  </div>
                )}
                {linkType !== "none" && ctaText && (
                  <span
                    style={{
                      marginTop: 10, alignSelf: "flex-start", padding: "5px 12px",
                      borderRadius: 12, background: "rgba(255,255,255,.94)", color: panel,
                      fontSize: 11, fontWeight: 600,
                    }}
                  >
                    {ctaText}
                  </span>
                )}
              </div>
              <div style={{ flex: "1 1 48%", minWidth: 0 }}>
                <Thumb src={image} alt="" height={128} />
              </div>
            </React.Fragment>
          ) : (
            <Thumb src={image} alt="" height={128} />
          )}
        </div>
        <div style={{ marginTop: 12, display: "flex", justifyContent: "center", gap: 6 }}>
          <span style={{ width: 18, height: 5, borderRadius: 3, background: "#fff" }} />
          <span style={{ width: 5, height: 5, borderRadius: 3, background: "rgba(255,255,255,.4)" }} />
          <span style={{ width: 5, height: 5, borderRadius: 3, background: "rgba(255,255,255,.4)" }} />
        </div>
        {!isActive && (
          <div style={{ marginTop: 10, textAlign: "center", fontSize: 12, color: "rgba(255,255,255,.6)" }}>
            Inactive — guests will not see this card.
          </div>
        )}
      </div>
    );
  }

  render() {
    const { isEditMode, isLoading, title, linkType, startDate, endDate, displayOrder } = this.state;
    const write = canWrite();

    if (isLoading) {
      return (
        <ConsolePage title="Edit banner" subtitle="Marketing / Banners">
          <div style={{ padding: "80px 0", textAlign: "center", color: C.taupeLight }}>
            Loading banner…
          </div>
        </ConsolePage>
      );
    }

    return (
      <ConsolePage
        title={isEditMode ? `Edit banner · ${title || ""}`.trim() : "New banner"}
        subtitle="Marketing / Banners"
        actions={
          <React.Fragment>
            <Btn kind="quiet" icon="arrow_back" onClick={() => this.props.history.push("/banner")}>
              Back to banners
            </Btn>
            {write && (
              <Btn icon="check" onClick={this.handleSubmit}>
                {isEditMode ? "Save banner" : "Create banner"}
              </Btn>
            )}
          </React.Fragment>
        }
      >
        <style dangerouslySetInnerHTML={{ __html: CSS }} />

        <div className="banner-editor" style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) 340px", gap: 20, alignItems: "start" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 18, minWidth: 0 }}>
            {this.renderBasics()}
            {this.renderLink()}
            {this.renderSchedule()}
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 18, position: "sticky", top: 100 }}>
            <Panel title="Guest app preview" style={{ padding: 20 }}>
              <div style={{ marginTop: 14 }}>{this.renderPreview()}</div>
            </Panel>

            <Panel title="Summary" style={{ padding: 20 }}>
              <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 11, fontSize: 14, color: C.slate }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                  <span>Slot</span>
                  <span style={{ color: C.ink }}>#{displayOrder}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                  <span>Tap action</span>
                  <span style={{ color: C.ink, textAlign: "right" }}>
                    {linkType === "none" && "None"}
                    {linkType === "coupon" && (this.getLinkedCouponName() || "Coupon")}
                    {linkType === "external" && "External URL"}
                    {linkType === "internal" && "App screen"}
                  </span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                  <span>Schedule</span>
                  <span style={{ color: C.ink, textAlign: "right" }}>
                    {startDate || "Now"} → {endDate || "No end"}
                  </span>
                </div>
              </div>
            </Panel>

            <Panel title="Placement" style={{ padding: 20 }}>
              <div style={{ marginTop: 12, fontSize: 13, color: C.taupe, lineHeight: 1.55 }}>
                Banners created here go to the guest app's home carousel. The
                editor sends <strong>home</strong> as the placement — other
                placements exist in the data model but are not offered yet.
              </div>
            </Panel>
          </div>
        </div>
      </ConsolePage>
    );
  }
}

export default BannerForm;
